/**
 * Ledion: lógica de los bloques propios.
 * - Zonas de instalación por código postal
 * - Instalación y extras añadidos al carrito junto a la pantalla
 * - Guía de pitch
 * - Refresco de IVA y disponibilidad al cambiar de variante
 * - Comprobador de código postal, prefill del presupuesto y NIF del carrito
 */
(() => {
  const configElement = document.getElementById('LedionConfig');
  if (!configElement) return;

  let config;
  try {
    config = JSON.parse(configElement.textContent);
  } catch (error) {
    console.error('Ledion: configuración no válida', error);
    return;
  }

  const strings = config.strings || {};
  const zones = (config.zones || []).filter((zone) => zone.name && zone.prefixes.length);

  /** Sustituye [clave] por su valor. */
  const fill = (template, values = {}) =>
    String(template || '').replace(/\[(\w+)\]/g, (match, key) => (key in values ? values[key] : match));

  const escapeHtml = (value) => {
    const span = document.createElement('span');
    span.textContent = String(value);
    return span.innerHTML;
  };

  /** Devuelve { valid, postcode, zone } para un código postal español. */
  const findZone = (raw) => {
    const postcode = String(raw || '').trim();
    if (!/^\d{5}$/.test(postcode)) return { valid: false };
    const prefix = postcode.slice(0, 2);
    const province = parseInt(prefix, 10);
    if (province < 1 || province > 52) return { valid: false };
    const zone = zones.find((candidate) => candidate.prefixes.includes(prefix)) || null;
    return { valid: true, postcode, zone };
  };

  const quoteLink = () =>
    config.quoteUrl ? ` <a href="${escapeHtml(config.quoteUrl)}">${escapeHtml(strings.quote_link || '')}</a>` : '';

  const setResult = (element, kind, html) => {
    if (!element) return;
    element.classList.toggle('ledion-result--ok', kind === 'ok');
    element.classList.toggle('ledion-result--warn', kind === 'warn');
    element.innerHTML = html || '';
  };

  window.Ledion = { findZone };

  /* ---------- Instalación ---------- */

  class LedionInstallation extends HTMLElement {
    connectedCallback() {
      this.mode = this.dataset.mode || 'addon';
      this.perUnit = this.dataset.perUnit === 'true';
      try {
        this.variants = JSON.parse(this.querySelector('[data-variants]')?.textContent || '[]');
      } catch (error) {
        this.variants = [];
      }
      this.postcodeWrap = this.querySelector('[data-postcode-wrap]');
      this.postcodeInput = this.querySelector('[data-postcode]');
      this.result = this.querySelector('.ledion-result');
      this.propertyMode = this.querySelector('[data-property-mode]');
      this.propertyPostcode = this.querySelector('[data-property-postcode]');

      this.addEventListener('change', () => this.update());
      this.postcodeInput?.addEventListener('input', () => this.update());
      this.update();
    }

    get modality() {
      if (this.mode === 'included') return 'included';
      const checked = this.querySelector('input[type="radio"]:checked');
      return checked ? checked.value : '';
    }

    get isVisible() {
      return this.offsetParent !== null;
    }

    state() {
      const modality = this.modality;
      if (!modality) return { needed: false, ok: true };

      const typed = this.postcodeInput ? this.postcodeInput.value.trim() : '';
      if (!typed) return { needed: true, ok: false, kind: '', message: escapeHtml(strings.postcode_prompt || '') };

      const lookup = findZone(typed);
      if (!lookup.valid) {
        return typed.length < 5
          ? { needed: true, ok: false, kind: '', message: escapeHtml(strings.postcode_prompt || '') }
          : { needed: true, ok: false, kind: 'warn', message: escapeHtml(strings.postcode_invalid || '') };
      }

      const outOfCoverage = {
        needed: true,
        ok: false,
        kind: 'warn',
        message: escapeHtml(strings.out_of_coverage || '') + quoteLink(),
      };
      if (!lookup.zone) return outOfCoverage;

      if (this.mode === 'included') {
        return {
          needed: true,
          ok: true,
          kind: 'ok',
          zone: lookup.zone,
          postcode: lookup.postcode,
          message: escapeHtml(fill(strings.included_ok, { zona: lookup.zone.name })),
        };
      }

      const variant = this.variants.find(
        (candidate) =>
          candidate.option1 === modality && (candidate.option2 === lookup.zone.name || candidate.option2 === null)
      );
      if (!variant || !variant.available) return outOfCoverage;

      const template = variant.price > 0 ? strings.install_price : strings.install_quote_later;
      return {
        needed: true,
        ok: true,
        kind: 'ok',
        zone: lookup.zone,
        postcode: lookup.postcode,
        variant,
        message: escapeHtml(fill(template, { zona: lookup.zone.name, precio: variant.price_formatted })),
      };
    }

    update() {
      const current = this.state();
      if (this.postcodeWrap) this.postcodeWrap.hidden = !current.needed;
      setResult(this.result, current.kind, current.needed ? current.message : '');

      const active = current.needed && current.ok;
      if (this.propertyMode) {
        this.propertyMode.disabled = !active;
        this.propertyMode.value =
          this.mode === 'included' ? this.dataset.includedLabel || 'Incluida' : this.modality;
      }
      if (this.propertyPostcode) {
        this.propertyPostcode.disabled = !active;
        this.propertyPostcode.value = active ? current.postcode : '';
      }
      return current;
    }

    /** Comprueba que se puede añadir al carrito. Si falta el código postal, lo pide. */
    validate() {
      const current = this.update();
      if (!current.needed || current.ok) return true;
      if (current.kind !== 'warn') setResult(this.result, 'warn', escapeHtml(strings.postcode_required || ''));
      this.postcodeInput?.focus();
      return false;
    }

    cartItems(quantity) {
      const current = this.state();
      if (this.mode !== 'addon' || !current.needed || !current.ok || !current.variant) return [];
      return [
        {
          id: current.variant.id,
          quantity: this.perUnit ? quantity : 1,
          properties: {
            [strings.property_for || 'Para']: this.dataset.productTitle || '',
            [strings.property_postcode || 'Código postal']: current.postcode,
          },
        },
      ];
    }
  }

  if (!customElements.get('ledion-installation')) {
    customElements.define('ledion-installation', LedionInstallation);
  }

  /* ---------- Añadir instalación y extras antes que la pantalla ---------- */

  const showFormError = (form, message) => {
    const productForm = form.closest('product-form');
    if (productForm && typeof productForm.handleErrorMessage === 'function') {
      productForm.handleErrorMessage(message);
    } else {
      console.error('Ledion:', message);
    }
  };

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || form.dataset.type !== 'add-to-cart-form') return;

      if (form.dataset.ledionPass === 'true') {
        delete form.dataset.ledionPass;
        return;
      }

      const scope = form.closest('product-info') || form.closest('.shopify-section') || document;
      const installation = scope.querySelector('ledion-installation');
      const activeInstallation = installation && installation.isVisible ? installation : null;
      const extras = Array.from(scope.querySelectorAll('input[data-ledion-extra]:checked')).filter(
        (input) => input.offsetParent !== null
      );
      if (!activeInstallation && !extras.length) return;

      if (activeInstallation && !activeInstallation.validate()) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      const quantity = parseInt(new FormData(form).get('quantity'), 10) || 1;
      const productTitle = activeInstallation?.dataset.productTitle || extras[0]?.dataset.productTitle || '';
      const items = activeInstallation ? activeInstallation.cartItems(quantity) : [];
      extras.forEach((input) => {
        items.push({
          id: Number(input.dataset.variantId),
          quantity: input.dataset.perUnit === 'true' ? quantity : 1,
          properties: { [strings.property_for || 'Para']: productTitle },
        });
      });
      if (!items.length) return;

      event.preventDefault();
      event.stopPropagation();

      const button = form.querySelector('[type="submit"]');
      if (button?.getAttribute('aria-disabled') === 'true') return;
      button?.setAttribute('aria-disabled', 'true');
      button?.classList.add('loading');

      fetch(`${window.routes.cart_add_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      })
        .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
        .then(({ ok, data }) => {
          if (!ok) throw new Error(data.description || data.message || strings.extras_error);
          button?.removeAttribute('aria-disabled');
          button?.classList.remove('loading');
          form.dataset.ledionPass = 'true';
          // En una tarea nueva: el navegador ignora un reenvío hecho mientras aún despacha el envío anterior.
          setTimeout(() => {
            if (typeof form.requestSubmit === 'function') {
              form.requestSubmit(button || undefined);
            } else {
              form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
            }
          }, 0);
        })
        .catch((error) => {
          button?.removeAttribute('aria-disabled');
          button?.classList.remove('loading');
          showFormError(form, error.message || strings.extras_error);
        });
    },
    true
  );

  /* ---------- Guía de pitch ---------- */

  class LedionPitchGuide extends HTMLElement {
    connectedCallback() {
      this.optionName = this.dataset.optionName || 'Pitch';
      this.result = this.querySelector('.ledion-result');
      this.addEventListener('change', (event) => {
        if (event.target instanceof HTMLInputElement && event.target.checked) this.recommend(event.target.value);
      });
    }

    recommend(pitch) {
      const scope = this.closest('product-info') || document;
      const picker = scope.querySelector('variant-selects');
      if (!picker) return;

      const radios = Array.from(picker.querySelectorAll('input[type="radio"]')).filter(
        (input) => input.dataset.optionName === this.optionName
      );
      const radio = radios.find((input) => input.value === pitch);
      const option = Array.from(picker.querySelectorAll('select option')).find(
        (candidate) => candidate.dataset.optionName === this.optionName && candidate.value === pitch
      );

      if (!radio && !option) {
        setResult(this.result, 'warn', escapeHtml(fill(strings.pitch_unavailable, { pitch })));
        return;
      }

      const unavailable = radio ? radio.classList.contains('disabled') : option.disabled;
      const template = unavailable ? strings.pitch_other_use : strings.pitch_recommended;
      setResult(this.result, unavailable ? 'warn' : 'ok', escapeHtml(fill(template, { pitch })));

      if (radio && !radio.checked) {
        radio.click();
      } else if (option && !option.selected) {
        const select = option.closest('select');
        select.value = option.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }

  if (!customElements.get('ledion-pitch-guide')) {
    customElements.define('ledion-pitch-guide', LedionPitchGuide);
  }

  /* ---------- Refresco al cambiar de variante ---------- */

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      const html = event?.data?.html;
      if (!html) return;
      document.querySelectorAll('[data-ledion-refresh]').forEach((element) => {
        const fresh = element.id ? html.getElementById(element.id) : null;
        if (fresh) element.innerHTML = fresh.innerHTML;
      });
    });
  }

  /* ---------- Comprobador de código postal ---------- */

  class LedionPostcodeCheck extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-postcode]');
      this.result = this.querySelector('.ledion-result');
      this.querySelector('form')?.addEventListener('submit', (event) => {
        event.preventDefault();
        this.check();
      });
    }

    check() {
      const lookup = findZone(this.input?.value);
      if (!lookup.valid) {
        setResult(this.result, 'warn', escapeHtml(strings.postcode_invalid || ''));
      } else if (!lookup.zone) {
        setResult(this.result, 'warn', escapeHtml(strings.out_of_coverage || '') + quoteLink());
      } else {
        setResult(this.result, 'ok', escapeHtml(fill(strings.included_ok, { zona: lookup.zone.name })));
      }
    }
  }

  if (!customElements.get('ledion-postcode-check')) {
    customElements.define('ledion-postcode-check', LedionPostcodeCheck);
  }

  /* ---------- Prefill del formulario de presupuesto ---------- */

  const params = new URLSearchParams(window.location.search);
  document.querySelectorAll('[data-ledion-prefill]').forEach((input) => {
    const value = params.get(input.dataset.ledionPrefill);
    if (value && !input.value) input.value = value;
  });

  /* ---------- NIF en el carrito ---------- */

  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.hasAttribute('data-ledion-nif')) return;
    fetch(`${window.routes.cart_update_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: { NIF: input.value.trim() } }),
    }).catch((error) => console.error('Ledion: no se pudo guardar el NIF', error));
  });
})();
